// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import MapView from '@arcgis/core/views/MapView';
import SceneView from '@arcgis/core/views/SceneView';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import Multipoint from '@arcgis/core/geometry/Multipoint';
import Polyline from '@arcgis/core/geometry/Polyline';
import Polygon from '@arcgis/core/geometry/Polygon';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import { buildLayerMap } from 'Features/dashboard/dashboardMetrics';
import { ChatMapHighlight, HighlightTarget } from 'Features/dashboard/dashboardSlice';

// Highlights the features Khora asked to emphasize. The PREFERRED path (highlight.targets) highlights
// the ALREADY-DISPLAYED features in place: it resolves each target layer in the loaded geodatabase,
// queries it for the features whose join key matches, then highlights THOSE — native layerView glow
// (which can never float, since it just tints the existing symbol) plus a colored outline drawn at the
// queried feature's REAL geometry. So the app owns feature display (heights/elevation) and Khora only
// outlines. The geojson path is a fallback (multi-layer sets, or when Khora didn't supply a key): it
// drapes a 2D outline on the terrain — never re-drawing Z, so a wrong feet/meters Z can't lift it.

type AnyView = MapView | SceneView;
type QueryableLayer = __esri.FeatureLayer | __esri.GeoJSONLayer;

// AirWorks Bright Blue (#01A1DD) — the default when the chat/chart doesn't ask for a specific color.
const DEFAULT_RGB = [1, 161, 221];
// Named colors Khora may reference in an answer ("highlight it in blue" / "in green"), so a highlight
// renders in the color that was asked for instead of one fixed scheme. Anything else parses as #RRGGBB.
const NAMED_RGB: Record<string, number[]> = {
  blue: [1, 161, 221], // AirWorks Bright Blue #01A1DD
  green: [52, 199, 89],
  red: [255, 59, 48],
  orange: [255, 148, 36], // AirWorks Bright Orange #FF9424
  yellow: [255, 214, 10],
  purple: [142, 68, 173],
  magenta: [255, 45, 146],
  pink: [255, 105, 180],
  cyan: [86, 228, 249],
  teal: [45, 152, 152],
  white: [255, 255, 255],
  gray: [142, 142, 147],
};

// Resolve a color name or #RRGGBB hex to an [r,g,b] triple; DEFAULT_RGB when absent/unrecognized.
const toRgb = (color?: string | null): number[] => {
  if (typeof color === 'string') {
    const key = color.trim().toLowerCase();
    if (NAMED_RGB[key]) return NAMED_RGB[key];
    const hex = key.replace(/^#/, '');
    if (/^[0-9a-f]{6}$/.test(hex)) {
      return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
    }
  }
  return DEFAULT_RGB;
};

// Per-feature color override — a `_color` property on a feature, for multi-color sets in one highlight.
const featureColor = (feature: GeoJSON.Feature): string | undefined => {
  const value = (feature.properties as Record<string, unknown> | null)?._color;
  return typeof value === 'string' ? value : undefined;
};

// Base highlight sizes (bumped up so the highlight reads on a busy map); `scale` is driven by the pulse
// so the marker/outline physically grows and shrinks.
const markerSymbol = (rgb: number[], scale = 1): SimpleMarkerSymbol => new SimpleMarkerSymbol({
  style: 'circle', size: 16 * scale, color: [...rgb, 0.9], outline: { color: [255, 255, 255, 1], width: 2 },
});
const lineSymbol = (rgb: number[], scale = 1): SimpleLineSymbol => new SimpleLineSymbol({ color: [...rgb, 1], width: 5 * scale });
// Polygon highlight is OUTLINE-ONLY (no fill) — matches how the app renders its own gdb polygons
// (simple-fill, style 'none') and prevents a big or overlapping polygon (e.g. A-BOUNDARY draped on the
// ground, or 365 stacking vegetation polys) from tinting the whole scene dark. A bright, thick outline
// traces the polygon; the interior is left untouched.
const fillSymbol = (rgb: number[], scale = 1): SimpleFillSymbol => new SimpleFillSymbol({ color: [...rgb, 0], style: 'none', outline: { color: [...rgb, 1], width: 5 * scale } });

const symbolFor = (geometry: __esri.Geometry, rgb: number[], scale = 1): SimpleMarkerSymbol | SimpleLineSymbol | SimpleFillSymbol => {
  if (geometry.type === 'polyline') return lineSymbol(rgb, scale);
  if (geometry.type === 'polygon' || geometry.type === 'extent') return fillSymbol(rgb, scale);
  return markerSymbol(rgb, scale);
};

// --------------------------------------------------------------------------- //
// Resolving + querying the already-displayed layer                            //
// --------------------------------------------------------------------------- //

const normalizeName = (name: string): string => name.toLowerCase().replace(/[^a-z0-9]/g, '');

// Published layers keep the gdb layer name as their title, but fall back to a normalized match so a
// minor rename (case/spacing/underscores) still resolves.
const resolveLayer = (layerMap: Map<string, QueryableLayer>, name: string): QueryableLayer | null => {
  const exact = layerMap.get(name);
  if (exact) return exact;
  const wanted = normalizeName(name);
  const entries = Array.from(layerMap.entries());
  const fuzzy = entries.find(([key]) => normalizeName(key) === wanted);
  return fuzzy ? fuzzy[1] : null;
};

// The layer's actual field name for `candidate` — published GeoJSON renames fields (Pole_ID -> "Pole ID"),
// so match on a normalized name and return the real name to use in the query.
const resolveFieldName = (layer: QueryableLayer, candidate: string): string => {
  const wanted = normalizeName(candidate);
  const match = (layer.fields || []).find((field) => normalizeName(field.name) === wanted);
  return match ? match.name : candidate;
};

const NUMERIC_FIELD_TYPES = ['integer', 'small-integer', 'single', 'double', 'long', 'oid', 'big-integer'];

// Build a `<field> IN (...)` where-clause. Values are quoted for text fields (single-quotes escaped) and
// left bare for numeric fields; the field name is double-quoted when it carries spaces/parens.
const buildInClause = (layer: QueryableLayer, field: string, values: string[]): string | null => {
  const fieldDef = (layer.fields || []).find((candidate) => candidate.name === field);
  const isNumeric = fieldDef ? NUMERIC_FIELD_TYPES.includes(fieldDef.type) : false;
  const list = values
    .map((value) => {
      if (isNumeric) {
        const num = Number(value);
        return Number.isFinite(num) ? String(num) : null;
      }
      return `'${String(value).replace(/'/g, "''")}'`;
    })
    .filter((entry): entry is string => entry != null);
  if (!list.length) return null;
  // Delimit a field name with special chars in double-quotes, escaping any embedded quote ("" ) so a
  // crafted key_field can't break out of the identifier and inject into the where-clause.
  const fieldRef = /[^A-Za-z0-9_]/.test(field) ? `"${field.replace(/"/g, '""')}"` : field;
  return `${fieldRef} IN (${list.join(',')})`;
};

// Query one target's already-displayed features (in the view's spatial reference, with Z) so they can be
// highlighted at their real geometry. Returns null when the layer/field/features can't be resolved.
const queryTarget = async (
  view: AnyView,
  layerMap: Map<string, QueryableLayer>,
  target: HighlightTarget,
): Promise<{ layer: QueryableLayer; features: __esri.Graphic[]; color?: string } | null> => {
  const layer = resolveLayer(layerMap, target.layer);
  if (!layer || !target.keyField || !target.keyValues?.length) return null;
  try {
    // Load BEFORE resolving the field: layer.fields (needed for renamed-key + numeric-type resolution) is
    // empty until load(), so building the where-clause first mis-resolves it and silently returns nothing.
    await layer.load();
    const where = buildInClause(layer, resolveFieldName(layer, target.keyField), target.keyValues);
    if (!where) return null;
    const query = layer.createQuery();
    query.where = where;
    query.outFields = ['*'];
    query.returnGeometry = true;
    query.returnZ = true; // Esri defaults returnZ to false; without it 3D poles/wires come back flat.
    query.outSpatialReference = view.spatialReference;
    const result = await layer.queryFeatures(query);
    return { layer, features: result.features, color: target.color };
  } catch (err) {
    // Don't swallow silently — a failed in-place query is exactly why a chart/target click "does nothing".
    // eslint-disable-next-line no-console
    console.warn('[khora highlight] queryTarget failed for', target.layer, err);
    return null; // the geojson fallback (if any) still shows something
  }
};

// GeoJSON geometry -> a 2D Esri geometry (Z dropped on purpose) for the draped fallback overlay.
const toEsriGeometry2D = (geometry: GeoJSON.Geometry | null): __esri.Geometry | null => {
  const SR = { wkid: 4326 };
  if (!geometry) return null;
  const drop = (coords: unknown): number[] => (Array.isArray(coords) ? [Number(coords[0]), Number(coords[1])] : []);
  const dropRing = (ring: unknown): number[][] => (Array.isArray(ring) ? ring.map(drop) : []);
  if (geometry.type === 'Point') return new Point({ x: geometry.coordinates[0], y: geometry.coordinates[1], spatialReference: SR });
  if (geometry.type === 'MultiPoint') return new Multipoint({ points: geometry.coordinates.map(drop), spatialReference: SR });
  if (geometry.type === 'LineString') return new Polyline({ paths: [geometry.coordinates.map(drop)], spatialReference: SR });
  if (geometry.type === 'MultiLineString') return new Polyline({ paths: geometry.coordinates.map(dropRing), spatialReference: SR });
  if (geometry.type === 'Polygon') return new Polygon({ rings: geometry.coordinates.map(dropRing), spatialReference: SR });
  if (geometry.type === 'MultiPolygon') return new Polygon({ rings: geometry.coordinates.map((poly) => poly.map(dropRing)).flat(), spatialReference: SR });
  return null;
};

// The gdb's Z is US survey FEET (its CRS is a state-plane foot system), but the 3D scene layers are
// METRIC — so an elevated highlight (wire/pole) must convert feet->meters or it floats ~3.3x too high.
// This applies ONLY to the geojson-fallback path (a keyless set with no displayed layer to query); the
// in-place path already reuses the displayed layer's own metric geometry, so it needs no conversion.
const HIGHLIGHT_Z_FT_TO_M = 0.3048006096; // 1 US survey foot in metres

// Deepest [x,y] or [x,y,z] position from a nested GeoJSON coordinate array.
const firstPosition = (coords: unknown): number[] | null => {
  if (!Array.isArray(coords)) return null;
  if (typeof coords[0] === 'number') return coords as number[];
  for (let i = 0; i < coords.length; i += 1) {
    const found = firstPosition(coords[i]);
    if (found) return found;
  }
  return null;
};

// True when a geometry carries a Z ordinate (poles/wires are 3D; roads/veg are flat).
const geometryHasZ = (geometry: GeoJSON.Geometry | null): boolean => {
  if (!geometry || !('coordinates' in geometry)) return false;
  const p = firstPosition((geometry as { coordinates: unknown }).coordinates);
  return !!p && p.length > 2 && p[2] != null;
};

// Scale every Z ordinate (feet->metres) in a nested GeoJSON coordinate array; x/y untouched.
const scaleZ = (coords: unknown): unknown => {
  if (!Array.isArray(coords)) return coords;
  if (typeof coords[0] === 'number') {
    const p = coords as number[];
    return p.length > 2 ? [p[0], p[1], p[2] * HIGHLIGHT_Z_FT_TO_M] : p;
  }
  return coords.map(scaleZ);
};

// GeoJSON geometry -> an Esri geometry that KEEPS Z (feet->metres) so an absolute-height overlay places
// the highlight at the feature's real elevation instead of draped on the ground.
const toEsriGeometry3D = (geometry: GeoJSON.Geometry | null): __esri.Geometry | null => {
  const SR = { wkid: 4326 };
  if (!geometry) return null;
  if (geometry.type === 'Point') {
    const [x, y, z] = geometry.coordinates;
    return z != null
      ? new Point({ x, y, z: z * HIGHLIGHT_Z_FT_TO_M, hasZ: true, spatialReference: SR })
      : new Point({ x, y, spatialReference: SR });
  }
  if (geometry.type === 'MultiPoint') return new Multipoint({ points: scaleZ(geometry.coordinates) as number[][], hasZ: true, spatialReference: SR });
  if (geometry.type === 'LineString') return new Polyline({ paths: [scaleZ(geometry.coordinates) as number[][]], hasZ: true, spatialReference: SR });
  if (geometry.type === 'MultiLineString') return new Polyline({ paths: scaleZ(geometry.coordinates) as number[][][], hasZ: true, spatialReference: SR });
  if (geometry.type === 'Polygon') return new Polygon({ rings: scaleZ(geometry.coordinates) as number[][][], hasZ: true, spatialReference: SR });
  if (geometry.type === 'MultiPolygon') return new Polygon({ rings: (scaleZ(geometry.coordinates) as number[][][][]).flat(), hasZ: true, spatialReference: SR });
  return null;
};

// --------------------------------------------------------------------------- //
// Active highlight lifecycle                                                  //
// --------------------------------------------------------------------------- //

// The current highlight's native handles + overlay layers, and a generation token that supersedes an
// in-flight (async) highlight when a newer one arrives.
let activeHandles: __esri.Handle[] = [];
let activeOverlays: GraphicsLayer[] = [];
let generation = 0;
// The view's native highlight tint (layerView.highlight) reads the VIEW-level highlightOptions color,
// not our per-call color — so to make the in-place glow match the clicked/hovered bar we set the view
// color for this highlight and restore the original on clear (other app highlights keep their color).
let savedHighlightOptions: unknown;
let highlightColorOverridden = false;

// Point the view's native-highlight color at `rgb` (saving the original once), so layerView.highlight
// glows in the bar/slice color instead of the fixed view default.
const setViewHighlightColor = (view: AnyView, rgb: number[]): void => {
  try {
    const current = (view as { highlightOptions?: { haloOpacity?: number; fillOpacity?: number } }).highlightOptions;
    if (!highlightColorOverridden) { savedHighlightOptions = current; highlightColorOverridden = true; }
    (view as { highlightOptions?: unknown }).highlightOptions = {
      color: rgb,
      haloOpacity: current?.haloOpacity ?? 1,
      fillOpacity: current?.fillOpacity ?? 0.25,
    };
  } catch {
    // highlightOptions not settable on this view — the colored outline overlay still matches the bar
  }
};

// Restore the view's original highlight color once our highlight is cleared.
const restoreViewHighlightColor = (view?: AnyView): void => {
  if (!highlightColorOverridden || !view) return;
  try {
    (view as { highlightOptions?: unknown }).highlightOptions = savedHighlightOptions;
  } catch {
    // view gone
  }
  highlightColorOverridden = false;
  savedHighlightOptions = undefined;
};

// Pulse: highlighted features BREATHE (their symbol grows/shrinks) so the highlight reads as a live
// "ping" rather than a static outline. LINES (poles/wires) pulse their outline WIDTH; POINTS are kept
// steady because per-frame marker swaps flickered/dropped point markers in the 3D SceneView. The breathe
// reassigns each graphic's symbol per frame, so it's capped to PULSE_MAX_SIZE_GRAPHICS (above that,
// static). The native layerView highlight stays steady underneath. PULSE_ENABLED off => fully static.
const PULSE_ENABLED = true;
const PULSE_PERIOD_MS = 2400; // one full breathe cycle (slower = calmer)
const PULSE_SIZE_MIN = 1; // marker scale at the trough (base size)
const PULSE_SIZE_MAX = 1.9; // marker scale at the peak
const PULSE_MAX_SIZE_GRAPHICS = 500; // above this many points, skip the size breathe (too costly)
let pulseRaf = 0;

// DIM_OTHER_LAYERS (flag): while a highlight is active, FADE every other loaded layer so the highlighted
// set is spotlit; reverted when the highlight clears (incl. the 5s auto-clear). NOTE: the Khora viewer is
// a 3D SceneView, where Esri's layer `effect` (grayscale/opacity filters) is NOT supported — so we dim via
// `layer.opacity` (which IS honored in 3D). Each layer's original opacity is saved + restored on undim.
const DIM_OTHER_LAYERS = true;
const DIM_OPACITY = 0.18; // faded — low enough to spotlight, not fully invisible
// When true, log what the highlighter does to the browser console so a "nothing shows" can be
// diagnosed without the map. Off in shipped code.
const HIGHLIGHT_DEBUG = false;
let dimmedLayers: { layer: QueryableLayer; opacity: number }[] = [];

const prefersReducedMotion = (): boolean => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

const stopPulse = (): void => {
  if (pulseRaf) {
    cancelAnimationFrame(pulseRaf);
    pulseRaf = 0;
  }
};

// Size-breathe the POINT graphics only (lines/polys stay steady). requestAnimationFrame is cancelled
// synchronously in stopPulse (called by clearActive before the overlays are destroyed), so no frame ever
// runs against a removed layer.
const startPulse = (pulseGraphics: { graphic: Graphic; rgb: number[] }[]): void => {
  stopPulse();
  if (!PULSE_ENABLED || prefersReducedMotion()) return;
  if (!pulseGraphics.length || pulseGraphics.length > PULSE_MAX_SIZE_GRAPHICS) return;
  const start = performance.now();
  const step = (now: number): void => {
    const wave = 0.5 + 0.5 * Math.sin(((now - start) / PULSE_PERIOD_MS) * Math.PI * 2);
    const scale = PULSE_SIZE_MIN + (PULSE_SIZE_MAX - PULSE_SIZE_MIN) * wave;
    pulseGraphics.forEach(({ graphic, rgb }) => {
      if (graphic.geometry) graphic.symbol = symbolFor(graphic.geometry, rgb, scale);
    });
    pulseRaf = requestAnimationFrame(step);
  };
  pulseRaf = requestAnimationFrame(step);
};

// Spotlight: fade every displayed layer (via opacity, honored in 3D) so the un-dimmed highlight overlay
// pops. Each layer's original opacity is saved so clearActive can restore it exactly.
// Only these geometry types dim cleanly via layer.opacity in a 3D SceneView. Polygon fills and TEXT
// symbol layers leave a baked-in dark render when their opacity is toggled ("opacity change not yet
// implemented in Graphics3D…SymbolLayer") that restoring opacity to 1 does NOT undo — so they're never
// dimmed. Polygons are outline-only, so leaving them at full opacity looks fine.
const DIMMABLE_GEOMETRIES = new Set(['point', 'multipoint', 'polyline']);

const dimOtherLayers = (layerMap: Map<string, QueryableLayer>): void => {
  if (!DIM_OTHER_LAYERS) return;
  undimLayers(); // clean slate first — NEVER store an already-dimmed opacity as a layer's "original"
  layerMap.forEach((layer) => {
    const { geometryType } = (layer as { geometryType?: string });
    if (!geometryType || !DIMMABLE_GEOMETRIES.has(geometryType)) return; // skip polygon/text/unknown
    try {
      const current = typeof layer.opacity === 'number' ? layer.opacity : 1;
      // Safety net: if a layer is ALREADY at the dim value (a prior revert didn't take, or state got
      // out of sync), treat its original as fully opaque so it can never lock at dimmed.
      const original = Math.abs(current - DIM_OPACITY) < 0.001 ? 1 : current;
      dimmedLayers.push({ layer, opacity: original });
      layer.opacity = DIM_OPACITY;
    } catch {
      // layer doesn't expose opacity — skip
    }
  });
};

const undimLayers = (): void => {
  if (HIGHLIGHT_DEBUG && dimmedLayers.length) {
    console.debug(
      '[khora highlight] undim -> restore',
      dimmedLayers.length,
      'layers; opacities:',
      dimmedLayers.slice(0, 8).map((d) => d.opacity),
    );
  }
  dimmedLayers.forEach(({ layer, opacity }) => {
    try {
      layer.opacity = opacity;
    } catch {
      // already gone
    }
  });
  dimmedLayers = [];
};

const clearActive = (view?: AnyView): void => {
  generation += 1; // supersede any in-flight highlight + cancel pending ttl timers
  stopPulse();
  undimLayers();
  restoreViewHighlightColor(view); // put the view's native-highlight color back to its default
  activeHandles.forEach((handle) => handle.remove());
  activeHandles = [];
  activeOverlays.forEach((overlay) => {
    try {
      view?.map?.remove(overlay);
      overlay.destroy();
    } catch {
      // already gone
    }
  });
  activeOverlays = [];
  // Defensive sweep: remove ANY highlight overlay still in the map — even one not tracked in
  // activeOverlays (e.g. leaked by a superseded/HMR-orphaned run). This GUARANTEES a highlight can
  // never persist past a clear, so the map always returns to exactly its pre-highlight state.
  try {
    const mapLayers = (view as { map?: { layers?: { toArray?: () => { title?: string; destroy?: () => void }[] } } })?.map?.layers;
    const stray = (mapLayers?.toArray?.() ?? []).filter((layer) => layer.title === '__khora_highlight');
    stray.forEach((layer) => {
      try {
        (view as { map?: { remove?: (l: unknown) => void } })?.map?.remove?.(layer);
        layer.destroy?.();
      } catch {
        // already gone
      }
    });
    if (HIGHLIGHT_DEBUG) console.debug('[khora highlight] cleared', { sweptStrayOverlays: stray.length, dimmedRemaining: dimmedLayers.length });
  } catch {
    // map not ready
  }
};

const showChatHighlight = async (
  view: AnyView | null,
  highlight: ChatMapHighlight,
  layerGroups: Parameters<typeof buildLayerMap>[0],
): Promise<void> => {
  clearActive(view ?? undefined);
  if (!view) return;
  const myGeneration = generation;
  const layerMap = buildLayerMap(layerGroups);
  if (HIGHLIGHT_DEBUG) {
    console.debug('[khora highlight] start', {
      targets: (highlight.targets || []).map((t) => ({ layer: t.layer, keyField: t.keyField, keys: t.keyValues?.length })),
      geojsonFeatures: highlight.geojson?.features?.length ?? 0,
      layersInMap: Array.from(layerMap.keys()),
    });
  }

  const overlays: GraphicsLayer[] = [];
  const handles: __esri.Handle[] = [];
  const pulseGraphics: { graphic: Graphic; rgb: number[] }[] = []; // graphics whose size the pulse breathes
  let totalFeatures = 0;
  let lastGeometry: __esri.Geometry | null = null;

  // Undo whatever this invocation added if a newer highlight supersedes it mid-flight. Deliberately does
  // NOT undim: dimOtherLayers runs only after the last generation check, so a superseded run never dimmed
  // before aborting — and the superseding run's clearActive already reverted any prior dim on entry.
  // Calling undimLayers() here would clobber the NEWER highlight's spotlight (rapid chart-click spam).
  const abort = (): void => {
    handles.forEach((handle) => handle.remove());
    overlays.forEach((overlay) => {
      try {
        view.map.remove(overlay);
        overlay.destroy();
      } catch {
        // already gone
      }
    });
  };

  // 1) Target path — highlight the already-displayed features in place at their real geometry.
  const targets: HighlightTarget[] = highlight.targets || [];
  const extraFallback: GeoJSON.Feature[] = []; // failed targets' shipped geometry, drawn in the fallback below
  // Query every target's displayed features in PARALLEL (independent round-trips) rather than one at a
  // time, so a multi-layer highlight resolves in the slowest single query's time, not their sum.
  const queriedTargets = await Promise.all(targets.map((target) => queryTarget(view, layerMap, target)));
  if (myGeneration !== generation) { abort(); return; }
  // Process the results IN ORDER: building overlays + native highlights mutates shared state
  // (handles / overlays / pulseGraphics / totals) and adds layers to the map, so keep that sequential.
  for (let index = 0; index < targets.length; index += 1) {
    const queried = queriedTargets[index];
    if (HIGHLIGHT_DEBUG) {
      console.debug(
        '[khora highlight] target',
        targets[index].layer,
        targets[index].keyField,
        '-> resolved',
        queried ? queried.features.length : 'LAYER/FIELD NOT FOUND',
        'features',
      );
    }
    if (!queried || !queried.features.length) {
      // In-place query resolved nothing (layer not displayed / key didn't match) — fall back to this
      // target's shipped geometry so the highlight is never silently lost.
      const fallback = targets[index].geojson?.features;
      if (fallback?.length) extraFallback.push(...fallback);
      // eslint-disable-next-line no-continue
      continue;
    }

    // The bar/slice color for BOTH the native glow and the outline, so the whole highlight matches it.
    const rgb = toRgb(queried.color);
    // Native layerView highlight — a glow on the existing symbol, so it can never float.
    try {
      // eslint-disable-next-line no-await-in-loop
      const layerView = await view.whenLayerView(queried.layer) as __esri.FeatureLayerView;
      if (myGeneration !== generation) { abort(); return; }
      setViewHighlightColor(view, rgb); // tint the native glow in the bar color, not the view default
      handles.push(layerView.highlight(queried.features));
    } catch (err) {
      // layer view not ready / not highlightable — the colored overlay below still shows it
      if (HIGHLIGHT_DEBUG) console.debug('[khora highlight] native highlight unavailable; using overlay', err);
    }

    // Colored outline at the queried (real) geometry. Match the source layer's elevation so a 3D
    // feature's outline sits exactly where the feature is drawn (no float); default by whether it has Z.
    const anyZ = queried.features.some((feature) => feature.geometry && (feature.geometry as { hasZ?: boolean }).hasZ);
    const overlay = new GraphicsLayer({
      title: '__khora_highlight',
      listMode: 'hide',
      // Match the source layer's elevation so the outline sits exactly where the feature is drawn; fall
      // back to absolute-height for a feature that carries Z, else drape it.
      elevationInfo: ((queried.layer as { elevationInfo?: unknown }).elevationInfo
        ?? { mode: anyZ ? 'absolute-height' : 'on-the-ground' }) as GraphicsLayer['elevationInfo'],
    });
    const withGeometry = queried.features.filter((feature) => Boolean(feature.geometry));
    withGeometry.forEach((feature) => {
      const graphic = new Graphic({ geometry: feature.geometry, symbol: symbolFor(feature.geometry, rgb) });
      overlay.add(graphic);
      // LINES (poles/wires) pulse — their outline width breathes; POINTS stay steady (marker swaps
      // flickered in the 3D SceneView).
      if (feature.geometry.type === 'polyline') pulseGraphics.push({ graphic, rgb });
    });
    // Tallied OUTSIDE the callback so no function-in-loop captures these mutable locals.
    totalFeatures += withGeometry.length;
    if (withGeometry.length) lastGeometry = withGeometry[withGeometry.length - 1].geometry;
    view.map.add(overlay);
    overlays.push(overlay);
  }

  // 2) Fallback path — a colored outline of untargeted-set geometry PLUS any targeted set whose in-place
  //    query resolved nothing (extraFallback). Targets that DID resolve are highlighted in place above
  //    (no double-draw). 3D features (poles/wires with Z) highlight at their REAL height via absolute-
  //    height with Z converted feet->metres; flat features (roads/veg) drape on the ground.
  const gjFeatures = [...(highlight.geojson?.features ?? []), ...extraFallback];
  if (gjFeatures.length) {
    const baseRgb = toRgb(highlight.color);
    // Draw a subset on its own overlay at a given elevation mode. Split by whether the feature carries Z:
    // 3D (wires/poles) -> absolute-height at real elevation; flat (roads / A-BOUNDARY / veg) -> draped
    // on the ground. Mixing them on ONE absolute-height overlay would drop the flat features to elevation
    // 0 — a surface at the bottom of the scene.
    const drawSubset = (subset: GeoJSON.Feature[], mode: 'absolute-height' | 'on-the-ground'): void => {
      if (!subset.length) return;
      const overlay = new GraphicsLayer({ title: '__khora_highlight', listMode: 'hide', elevationInfo: { mode } });
      subset.forEach((feature) => {
        const geometry = mode === 'absolute-height' ? toEsriGeometry3D(feature.geometry) : toEsriGeometry2D(feature.geometry);
        if (!geometry) return;
        const perFeature = featureColor(feature);
        const rgb = perFeature ? toRgb(perFeature) : baseRgb;
        const graphic = new Graphic({ geometry, symbol: symbolFor(geometry, rgb) });
        overlay.add(graphic);
        if (geometry.type === 'polyline') pulseGraphics.push({ graphic, rgb }); // lines (poles/wires) pulse
        totalFeatures += 1;
        lastGeometry = geometry;
      });
      if (overlay.graphics.length) {
        view.map.add(overlay);
        overlays.push(overlay);
      }
    };
    drawSubset(gjFeatures.filter((feature) => geometryHasZ(feature.geometry)), 'absolute-height');
    drawSubset(gjFeatures.filter((feature) => !geometryHasZ(feature.geometry)), 'on-the-ground');
  }

  if (myGeneration !== generation) { abort(); return; }
  activeHandles = handles;
  activeOverlays = overlays;
  if (!totalFeatures) return;
  startPulse(pulseGraphics); // flash the POINTS (marker size breathe); lines/polys stay steady
  dimOtherLayers(layerMap); // spotlight (behind DIM_OTHER_LAYERS flag) — reverted in clearActive
  if (HIGHLIGHT_DEBUG) {
    const g0 = overlays[0]?.graphics?.getItemAt?.(0);
    const ext = (g0?.geometry as { extent?: __esri.Extent })?.extent;
    const viewExt = (view as { extent?: __esri.Extent }).extent;
    console.debug('[khora highlight] drawn', {
      features: totalFeatures,
      overlays: overlays.length,
      overlaysInMap: overlays.filter((o) => view.map.layers.includes(o)).length,
      firstGeomType: g0?.geometry?.type,
      firstGeomCenter: ext ? [Number(ext.center.x.toFixed(4)), Number(ext.center.y.toFixed(4))] : null,
      viewSR: view.spatialReference?.wkid,
      viewCenter: viewExt ? [Number(viewExt.center.x.toFixed(4)), Number(viewExt.center.y.toFixed(4))] : null,
      dimmedLayers: dimmedLayers.length,
    });
  }

  // Transient highlights (e.g. a bar click passes ttlMs=5000) auto-clear after their lifetime — unless a
  // newer highlight already replaced them (clearActive bumps the generation, so the stale timer no-ops).
  if (highlight.ttlMs && highlight.ttlMs > 0) {
    const ttlGeneration = generation;
    setTimeout(() => { if (generation === ttlGeneration) clearActive(view); }, highlight.ttlMs);
  }

  // Camera policy — the highlight makes features visible IN PLACE, so don't yank the camera. Only a
  // single feature ("show pole 23") is gently centered; a multi-feature set is left where it is (fitting
  // the whole spread would zoom the camera out).
  if (totalFeatures === 1 && lastGeometry) {
    view.goTo({ target: lastGeometry }, { duration: 700, easing: 'ease-in-out' }).catch(() => {});
  }
};

export const clearChatHighlight = (view?: AnyView | null): void => clearActive(view ?? undefined);
export default showChatHighlight;
